import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DEFAULT_SITE } from '../siteDefaults';
import { resolveHistory } from '../siteHistory';
import { fetchSite, getToken, patchHistory, uploadFile } from './api';
import { AdminImagePreview, AdminModal, fileInput, input, label } from './adminUi';

function newMilestoneSortId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `ms-${crypto.randomUUID()}`;
  }
  return `ms-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Stable row ids for drag-and-drop (stripped before save). */
function ensureMilestoneSortIds(formLike) {
  const t = formLike.timeline || {};
  const milestones = Array.isArray(t.milestones) ? t.milestones : [];
  return {
    ...formLike,
    timeline: {
      ...t,
      milestones: milestones.map((m) => ({
        ...m,
        _id: typeof m?._id === 'string' && m._id.trim() ? m._id.trim() : newMilestoneSortId(),
      })),
    },
  };
}

function emptyMilestone() {
  return { _id: newMilestoneSortId(), year: '', title: '', body: '', image: '', imageAlt: '' };
}

const ghostBtnClass =
  'rounded-lg px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100';

function DragHandleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <circle cx="6" cy="5" r="1.75" />
      <circle cx="14" cy="5" r="1.75" />
      <circle cx="6" cy="10" r="1.75" />
      <circle cx="14" cy="10" r="1.75" />
      <circle cx="6" cy="15" r="1.75" />
      <circle cx="14" cy="15" r="1.75" />
    </svg>
  );
}

function MilestoneDragPreview({ row }) {
  const y = row?.year?.trim() || '—';
  const t = row?.title?.trim() || 'Milestone';
  return (
    <div className="w-full max-w-2xl cursor-grabbing rounded-xl border-2 border-brand-olive/50 bg-white p-4 shadow-2xl ring-4 ring-brand-olive/15">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-olive">
        <DragHandleIcon className="h-4 w-4 shrink-0 text-brand-forest" />
        Reorder milestone
      </p>
      <p className="mt-2 font-mono text-lg font-bold tabular-nums text-brand-forest">{y}</p>
      <p className="mt-1 truncate text-sm font-semibold text-neutral-800">{t}</p>
    </div>
  );
}

function SortableMilestoneRow({ id, index, row, onEdit, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const label = row?.title?.trim() || `Milestone ${index + 1}`;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex flex-wrap items-stretch gap-2 rounded-lg border bg-neutral-50 px-2 py-2 sm:items-center sm:px-3 ${
        isDragging ? 'border-dashed border-brand-olive/50 bg-brand-cream/40 opacity-60' : 'border-neutral-100'
      }`}
    >
      <div className="flex shrink-0 items-center justify-center self-center">
        <button
          type="button"
          title="Drag to reorder"
          aria-label={`Drag to reorder: ${label}`}
          className="inline-flex cursor-grab touch-none items-center justify-center rounded-lg border border-neutral-200 bg-white p-2 text-neutral-500 shadow-sm hover:border-brand-olive/40 hover:bg-brand-lime-soft/30 hover:text-brand-forest active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <DragHandleIcon className="h-5 w-5 shrink-0 select-none" />
        </button>
      </div>
      <div className="min-w-0 flex-1 py-0.5">
        <span className="font-semibold tabular-nums text-neutral-900">{row.year || '—'}</span>
        <span className="mx-2 text-neutral-300">·</span>
        <span className="text-sm text-neutral-800">{clip(row.title, 64)}</span>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1 self-center">
        <button type="button" className={ghostBtnClass} onClick={() => onEdit(index)}>
          Edit
        </button>
        <button type="button" className={`${ghostBtnClass} text-red-700`} onClick={() => onRemove(index)}>
          Remove
        </button>
      </div>
    </li>
  );
}

const cardClass =
  'rounded-xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5';
const primaryBtnClass =
  'rounded-lg bg-brand-olive px-3 py-2 text-sm font-semibold text-white hover:opacity-95 active:opacity-90 disabled:opacity-50';
const secondaryBtnClass =
  'rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50';

function ModalFooter({ onCancel, onApply, applyLabel = 'Apply', applyDisabled = false }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" onClick={onCancel} className={secondaryBtnClass}>
        Cancel
      </button>
      <button type="button" onClick={onApply} disabled={applyDisabled} className={primaryBtnClass}>
        {applyLabel}
      </button>
    </div>
  );
}

function clip(s, n) {
  if (typeof s !== 'string' || !s.trim()) return '—';
  const t = s.trim();
  return t.length <= n ? t : `${t.slice(0, n)}…`;
}

export default function HistoryDashboard() {
  const navigate = useNavigate();
  const [form, setForm] = useState(() => ensureMilestoneSortIds(resolveHistory(DEFAULT_SITE)));
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadKey, setUploadKey] = useState('');

  const [modalErr, setModalErr] = useState('');

  const [heroOpen, setHeroOpen] = useState(false);
  const [heroDraft, setHeroDraft] = useState(null);

  const [internalOpen, setInternalOpen] = useState(false);
  const [internalDraft, setInternalDraft] = useState(null);

  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineDraft, setTimelineDraft] = useState(null);

  const [bulletOpen, setBulletOpen] = useState(false);
  const [bulletDraft, setBulletDraft] = useState('');
  const [bulletIndex, setBulletIndex] = useState(null);

  const [milestoneOpen, setMilestoneOpen] = useState(false);
  const [milestoneDraft, setMilestoneDraft] = useState(null);
  const [milestoneIndex, setMilestoneIndex] = useState(null);

  const [activeMilestoneId, setActiveMilestoneId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSite()
      .then((site) => setForm(ensureMilestoneSortIds(resolveHistory(site))))
      .catch(() => setForm(ensureMilestoneSortIds(resolveHistory(DEFAULT_SITE))));
  }, [navigate]);

  function openHeroModal() {
    setModalErr('');
    setHeroDraft({
      pageHeroImage: form.pageHeroImage || '',
      heroTitle: form.heroTitle || '',
      heroBreadcrumbHome: form.heroBreadcrumbHome || '',
      heroBreadcrumbCurrent: form.heroBreadcrumbCurrent || '',
    });
    setHeroOpen(true);
  }

  function applyHeroModal() {
    if (!heroDraft) return;
    setForm((f) => ({
      ...f,
      pageHeroImage: heroDraft.pageHeroImage || '',
      heroTitle: heroDraft.heroTitle || '',
      heroBreadcrumbHome: heroDraft.heroBreadcrumbHome || '',
      heroBreadcrumbCurrent: heroDraft.heroBreadcrumbCurrent || '',
    }));
    setHeroOpen(false);
    setHeroDraft(null);
    setModalErr('');
  }

  function openInternalModal() {
    setModalErr('');
    const int = form.internal || {};
    setInternalDraft({
      eyebrow: int.eyebrow || '',
      title: int.title || '',
      body: int.body || '',
      image: int.image || '',
      imageAlt: int.imageAlt || '',
      caption: int.caption || '',
    });
    setInternalOpen(true);
  }

  function applyInternalModal() {
    if (!internalDraft) return;
    setForm((f) => ({
      ...f,
      internal: {
        ...f.internal,
        ...internalDraft,
        bullets: Array.isArray(f.internal?.bullets) ? f.internal.bullets : [],
      },
    }));
    setInternalOpen(false);
    setInternalDraft(null);
    setModalErr('');
  }

  function openTimelineModal() {
    setModalErr('');
    const t = form.timeline || {};
    setTimelineDraft({
      eyebrow: t.eyebrow || '',
      title: t.title || '',
      intro: t.intro || '',
    });
    setTimelineOpen(true);
  }

  function applyTimelineModal() {
    if (!timelineDraft) return;
    setForm((f) => ({
      ...f,
      timeline: {
        ...f.timeline,
        eyebrow: timelineDraft.eyebrow || '',
        title: timelineDraft.title || '',
        intro: timelineDraft.intro || '',
      },
    }));
    setTimelineOpen(false);
    setTimelineDraft(null);
    setModalErr('');
  }

  function openBulletModal(index) {
    setModalErr('');
    const bullets = Array.isArray(form.internal?.bullets) ? [...form.internal.bullets] : [];
    setBulletIndex(index);
    setBulletDraft(index == null ? '' : typeof bullets[index] === 'string' ? bullets[index] : '');
    setBulletOpen(true);
  }

  function applyBulletModal() {
    const text = typeof bulletDraft === 'string' ? bulletDraft.trim() : '';
    if (!text) {
      setModalErr('Enter bullet text.');
      return;
    }
    setForm((f) => {
      const prev = Array.isArray(f.internal?.bullets) ? [...f.internal.bullets] : [];
      if (bulletIndex == null) {
        prev.push(text);
      } else {
        prev[bulletIndex] = text;
      }
      return { ...f, internal: { ...f.internal, bullets: prev } };
    });
    setBulletOpen(false);
    setBulletDraft('');
    setBulletIndex(null);
    setModalErr('');
  }

  function removeBullet(i) {
    if (!window.confirm('Remove this bullet?')) return;
    setForm((f) => ({
      ...f,
      internal: {
        ...f.internal,
        bullets: (f.internal?.bullets || []).filter((_, j) => j !== i),
      },
    }));
  }

  function openMilestoneModal(index) {
    setModalErr('');
    const milestones = form.timeline?.milestones || [];
    if (index == null) {
      setMilestoneDraft(emptyMilestone());
      setMilestoneIndex(null);
    } else {
      const m = milestones[index] || emptyMilestone();
      setMilestoneDraft({ ...emptyMilestone(), ...m });
      setMilestoneIndex(index);
    }
    setMilestoneOpen(true);
  }

  function applyMilestoneModal() {
    if (!milestoneDraft) return;
    const year = String(milestoneDraft.year ?? '').trim();
    const title = String(milestoneDraft.title ?? '').trim();
    if (!year || !title) {
      setModalErr('Year and title are required.');
      return;
    }
    const sortId =
      String(milestoneDraft._id ?? '').trim() || newMilestoneSortId();
    const row = {
      _id: sortId,
      year,
      title,
      body: String(milestoneDraft.body ?? '').trim(),
      image: String(milestoneDraft.image ?? '').trim(),
      imageAlt: String(milestoneDraft.imageAlt ?? '').trim(),
    };
    setForm((f) => {
      const milestones = [...(f.timeline?.milestones || [])];
      if (milestoneIndex == null) {
        milestones.push(row);
      } else {
        milestones[milestoneIndex] = row;
      }
      return { ...f, timeline: { ...f.timeline, milestones } };
    });
    setMilestoneOpen(false);
    setMilestoneDraft(null);
    setMilestoneIndex(null);
    setModalErr('');
  }

  function removeMilestone(i) {
    if (!window.confirm('Remove this milestone?')) return;
    setForm((f) => {
      const milestones = [...(f.timeline?.milestones || [])];
      milestones.splice(i, 1);
      return { ...f, timeline: { ...f.timeline, milestones } };
    });
  }

  function handleMilestoneDragStart(event) {
    setActiveMilestoneId(String(event.active.id));
  }

  function handleMilestoneDragEnd(event) {
    setActiveMilestoneId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setForm((f) => {
      const milestones = [...(f.timeline?.milestones || [])];
      const oldIndex = milestones.findIndex((m) => m._id === active.id);
      const newIndex = milestones.findIndex((m) => m._id === over.id);
      if (oldIndex < 0 || newIndex < 0) return f;
      return { ...f, timeline: { ...f.timeline, milestones: arrayMove(milestones, oldIndex, newIndex) } };
    });
  }

  function handleMilestoneDragCancel() {
    setActiveMilestoneId(null);
  }

  async function runUpload(file, applyUrl) {
    if (!file) return;
    setErr('');
    setUploadKey('busy');
    try {
      const { url } = await uploadFile(file);
      applyUrl(url);
    } catch (e) {
      setErr(e.message || 'Upload failed');
    } finally {
      setUploadKey('');
    }
  }

  async function save() {
    setMsg('');
    setErr('');
    const bullets = (form.internal?.bullets || [])
      .map((b) => (typeof b === 'string' ? b.trim() : ''))
      .filter(Boolean);
    setSaving(true);
    try {
      const payload = {
        pageHeroImage: form.pageHeroImage || '',
        heroTitle: form.heroTitle || '',
        heroBreadcrumbHome: form.heroBreadcrumbHome || '',
        heroBreadcrumbCurrent: form.heroBreadcrumbCurrent || '',
        internal: {
          ...form.internal,
          bullets,
        },
        timeline: {
          ...form.timeline,
          milestones: (form.timeline?.milestones || [])
            .filter((m) => m && String(m.year ?? '').trim() && String(m.title ?? '').trim())
            .map(({ _id: _sortId, ...rest }) => rest),
        },
      };
      const { history } = await patchHistory(payload);
      setForm(ensureMilestoneSortIds(resolveHistory({ history: history || payload })));
      setMsg('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const uploading = uploadKey === 'busy';
  const bullets = Array.isArray(form.internal?.bullets) ? form.internal.bullets : [];
  const milestones = form.timeline?.milestones || [];
  const milestoneIds = milestones.map((m) => m._id).filter(Boolean);
  const activeMilestoneRow = activeMilestoneId ? milestones.find((m) => m._id === activeMilestoneId) : null;

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-bold text-neutral-900">History page</h1>
          <p className="text-sm text-neutral-600">Edit content in dialogs; save when you are ready to publish.</p>
        </div>
        <button type="button" disabled={saving} onClick={save} className={primaryBtnClass}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </header>

      <div className="min-h-0 w-full min-w-0 flex-1 overflow-y-auto px-4 py-6 md:px-8">
        {msg ? (
          <p className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{msg}</p>
        ) : null}
        {err ? (
          <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</p>
        ) : null}

        <div className="space-y-4">
          <section className={cardClass}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="m-0 text-base font-bold text-neutral-900">Hero</h2>
                <p className="mt-1 text-sm text-neutral-600">
                  {clip(form.heroTitle, 48)} · {clip(form.heroBreadcrumbHome, 12)} / {clip(form.heroBreadcrumbCurrent, 16)}
                </p>
              </div>
              <button type="button" onClick={openHeroModal} className={secondaryBtnClass}>
                Edit in dialog
              </button>
            </div>
            {form.pageHeroImage ? (
              <div className="mt-4 max-w-xs">
                <AdminImagePreview src={form.pageHeroImage} alt="" emptyLabel="No hero image" />
              </div>
            ) : null}
          </section>

          <section className={cardClass}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="m-0 text-base font-bold text-neutral-900">Internal section</h2>
                <p className="mt-1 text-sm text-neutral-600">{clip(form.internal?.title, 60)}</p>
              </div>
              <button type="button" onClick={openInternalModal} className={secondaryBtnClass}>
                Edit in dialog
              </button>
            </div>
            <p className="mt-3 text-xs text-neutral-500">{bullets.length} bullet{bullets.length === 1 ? '' : 's'}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={() => openBulletModal(null)} className={secondaryBtnClass}>
                Add bullet
              </button>
            </div>
            {bullets.length ? (
              <ul className="mt-3 list-none space-y-2 p-0">
                {bullets.map((b, i) => (
                  <li
                    key={`b-${i}-${String(b).slice(0, 24)}`}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-neutral-100 bg-neutral-50 px-3 py-2 text-sm"
                  >
                    <span className="min-w-0 flex-1 text-neutral-800">{clip(b, 120)}</span>
                    <span className="flex shrink-0 gap-1">
                      <button type="button" className={ghostBtnClass} onClick={() => openBulletModal(i)}>
                        Edit
                      </button>
                      <button type="button" className={`${ghostBtnClass} text-red-700`} onClick={() => removeBullet(i)}>
                        Remove
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-neutral-500">No bullets yet. Use Add bullet.</p>
            )}
          </section>

          <section className={cardClass}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="m-0 text-base font-bold text-neutral-900">Timeline header</h2>
                <p className="mt-1 text-sm text-neutral-600">{clip(form.timeline?.title, 56)}</p>
              </div>
              <button type="button" onClick={openTimelineModal} className={secondaryBtnClass}>
                Edit in dialog
              </button>
            </div>
          </section>

          <section className={cardClass}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="m-0 text-base font-bold text-neutral-900">Milestones</h2>
                <p className="mt-1 text-xs text-neutral-500">
                  Use the six-dot handle to drag and reorder. Save when you are ready to publish.
                </p>
              </div>
              <button type="button" onClick={() => openMilestoneModal(null)} className={secondaryBtnClass}>
                Add milestone
              </button>
            </div>
            {milestones.length === 0 ? (
              <p className="mt-3 text-sm text-neutral-500">No milestones yet. Add one with the button above.</p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={handleMilestoneDragStart}
                onDragEnd={handleMilestoneDragEnd}
                onDragCancel={handleMilestoneDragCancel}
              >
                <SortableContext items={milestoneIds} strategy={verticalListSortingStrategy}>
                  <ul className="mt-4 list-none space-y-2 p-0">
                    {milestones.map((m, i) => (
                      <SortableMilestoneRow
                        key={m._id}
                        id={m._id}
                        index={i}
                        row={m}
                        onEdit={openMilestoneModal}
                        onRemove={removeMilestone}
                      />
                    ))}
                  </ul>
                </SortableContext>
                <DragOverlay
                  dropAnimation={{
                    duration: 220,
                    easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
                  }}
                  style={{ cursor: 'grabbing' }}
                >
                  {activeMilestoneRow ? <MilestoneDragPreview row={activeMilestoneRow} /> : null}
                </DragOverlay>
              </DndContext>
            )}
          </section>
        </div>
      </div>

      <AdminModal
        open={heroOpen}
        title="Hero"
        description="Page hero image, title, and breadcrumb labels."
        error={modalErr}
        onClose={() => {
          setHeroOpen(false);
          setHeroDraft(null);
          setModalErr('');
        }}
        footer={
          <ModalFooter
            onCancel={() => {
              setHeroOpen(false);
              setHeroDraft(null);
              setModalErr('');
            }}
            onApply={applyHeroModal}
          />
        }
      >
        {heroDraft ? (
          <div className="grid gap-4 md:grid-cols-2">
            <label className={`${label} md:col-span-2`}>
              Page hero image URL
              <input
                className={input}
                value={heroDraft.pageHeroImage}
                onChange={(e) => setHeroDraft((d) => ({ ...d, pageHeroImage: e.target.value }))}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Upload hero image
              <input
                type="file"
                accept="image/*"
                disabled={uploading}
                className={fileInput}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  runUpload(f, (url) => setHeroDraft((d) => ({ ...d, pageHeroImage: url })));
                }}
              />
            </label>
            <AdminImagePreview src={heroDraft.pageHeroImage} alt="" />
            <label className={`${label} md:col-span-2`}>
              Title (H1)
              <input
                className={input}
                value={heroDraft.heroTitle}
                onChange={(e) => setHeroDraft((d) => ({ ...d, heroTitle: e.target.value }))}
              />
            </label>
            <label className={label}>
              Breadcrumb — home label
              <input
                className={input}
                value={heroDraft.heroBreadcrumbHome}
                onChange={(e) => setHeroDraft((d) => ({ ...d, heroBreadcrumbHome: e.target.value }))}
              />
            </label>
            <label className={label}>
              Breadcrumb — current
              <input
                className={input}
                value={heroDraft.heroBreadcrumbCurrent}
                onChange={(e) => setHeroDraft((d) => ({ ...d, heroBreadcrumbCurrent: e.target.value }))}
              />
            </label>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={internalOpen}
        title="Internal section"
        description="Eyebrow, title, body, image, caption."
        error={modalErr}
        onClose={() => {
          setInternalOpen(false);
          setInternalDraft(null);
          setModalErr('');
        }}
        footer={
          <ModalFooter
            onCancel={() => {
              setInternalOpen(false);
              setInternalDraft(null);
              setModalErr('');
            }}
            onApply={applyInternalModal}
          />
        }
      >
        {internalDraft ? (
          <div className="grid gap-4 md:grid-cols-2">
            <label className={label}>
              Eyebrow
              <input
                className={input}
                value={internalDraft.eyebrow}
                onChange={(e) => setInternalDraft((d) => ({ ...d, eyebrow: e.target.value }))}
              />
            </label>
            <label className={label}>
              Title
              <input
                className={input}
                value={internalDraft.title}
                onChange={(e) => setInternalDraft((d) => ({ ...d, title: e.target.value }))}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Body
              <textarea
                className={`${input} min-h-[6rem]`}
                value={internalDraft.body}
                onChange={(e) => setInternalDraft((d) => ({ ...d, body: e.target.value }))}
              />
            </label>
            <label className={label}>
              Image URL
              <input
                className={input}
                value={internalDraft.image}
                onChange={(e) => setInternalDraft((d) => ({ ...d, image: e.target.value }))}
              />
            </label>
            <label className={label}>
              Upload image
              <input
                type="file"
                accept="image/*"
                disabled={uploading}
                className={fileInput}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  runUpload(f, (url) => setInternalDraft((d) => ({ ...d, image: url })));
                }}
              />
            </label>
            <label className={label}>
              Image alt
              <input
                className={input}
                value={internalDraft.imageAlt}
                onChange={(e) => setInternalDraft((d) => ({ ...d, imageAlt: e.target.value }))}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Image caption
              <input
                className={input}
                value={internalDraft.caption}
                onChange={(e) => setInternalDraft((d) => ({ ...d, caption: e.target.value }))}
              />
            </label>
            <div className="md:col-span-2">
              <AdminImagePreview src={internalDraft.image} alt={internalDraft.imageAlt || ''} />
            </div>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={timelineOpen}
        title="Timeline header"
        description="Eyebrow, title, and intro above the milestone list."
        error={modalErr}
        onClose={() => {
          setTimelineOpen(false);
          setTimelineDraft(null);
          setModalErr('');
        }}
        footer={
          <ModalFooter
            onCancel={() => {
              setTimelineOpen(false);
              setTimelineDraft(null);
              setModalErr('');
            }}
            onApply={applyTimelineModal}
          />
        }
      >
        {timelineDraft ? (
          <div className="grid gap-4">
            <label className={label}>
              Eyebrow
              <input
                className={input}
                value={timelineDraft.eyebrow}
                onChange={(e) => setTimelineDraft((d) => ({ ...d, eyebrow: e.target.value }))}
              />
            </label>
            <label className={label}>
              Title
              <input
                className={input}
                value={timelineDraft.title}
                onChange={(e) => setTimelineDraft((d) => ({ ...d, title: e.target.value }))}
              />
            </label>
            <label className={label}>
              Intro
              <textarea
                className={`${input} min-h-[5rem]`}
                value={timelineDraft.intro}
                onChange={(e) => setTimelineDraft((d) => ({ ...d, intro: e.target.value }))}
              />
            </label>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={bulletOpen}
        title={bulletIndex == null ? 'Add bullet' : 'Edit bullet'}
        description="One line of supporting copy for the internal section."
        error={modalErr}
        onClose={() => {
          setBulletOpen(false);
          setBulletDraft('');
          setBulletIndex(null);
          setModalErr('');
        }}
        footer={
          <ModalFooter
            onCancel={() => {
              setBulletOpen(false);
              setBulletDraft('');
              setBulletIndex(null);
              setModalErr('');
            }}
            onApply={applyBulletModal}
          />
        }
      >
        <label className={label}>
          Bullet text
          <textarea
            className={`${input} min-h-[5rem]`}
            value={bulletDraft}
            onChange={(e) => {
              setBulletDraft(e.target.value);
              setModalErr('');
            }}
            placeholder="e.g. Quality labs and dispatch workflows aligned with national seed standards."
          />
        </label>
      </AdminModal>

      <AdminModal
        open={milestoneOpen}
        title={milestoneIndex == null ? 'Add milestone' : 'Edit milestone'}
        description="Year and title are required. Other fields are optional."
        error={modalErr}
        onClose={() => {
          setMilestoneOpen(false);
          setMilestoneDraft(null);
          setMilestoneIndex(null);
          setModalErr('');
        }}
        footer={
          <ModalFooter
            onCancel={() => {
              setMilestoneOpen(false);
              setMilestoneDraft(null);
              setMilestoneIndex(null);
              setModalErr('');
            }}
            onApply={applyMilestoneModal}
          />
        }
      >
        {milestoneDraft ? (
          <div className="grid gap-4 md:grid-cols-2">
            <label className={label}>
              Year
              <input
                className={input}
                value={milestoneDraft.year}
                onChange={(e) => {
                  setMilestoneDraft((d) => ({ ...d, year: e.target.value }));
                  setModalErr('');
                }}
              />
            </label>
            <label className={label}>
              Title
              <input
                className={input}
                value={milestoneDraft.title}
                onChange={(e) => {
                  setMilestoneDraft((d) => ({ ...d, title: e.target.value }));
                  setModalErr('');
                }}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Body
              <textarea
                className={`${input} min-h-[5rem]`}
                value={milestoneDraft.body}
                onChange={(e) => setMilestoneDraft((d) => ({ ...d, body: e.target.value }))}
              />
            </label>
            <label className={label}>
              Image URL
              <input
                className={input}
                value={milestoneDraft.image}
                onChange={(e) => setMilestoneDraft((d) => ({ ...d, image: e.target.value }))}
              />
            </label>
            <label className={label}>
              Upload image
              <input
                type="file"
                accept="image/*"
                disabled={uploading}
                className={fileInput}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  runUpload(f, (url) => setMilestoneDraft((d) => ({ ...d, image: url })));
                }}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Image alt
              <input
                className={input}
                value={milestoneDraft.imageAlt}
                onChange={(e) => setMilestoneDraft((d) => ({ ...d, imageAlt: e.target.value }))}
              />
            </label>
            <div className="md:col-span-2">
              <AdminImagePreview src={milestoneDraft.image} alt={milestoneDraft.imageAlt || ''} />
            </div>
          </div>
        ) : null}
      </AdminModal>
    </>
  );
}
